package com.swetdot.swetgram.ui.moderation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.window.Dialog
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.swetdot.swetgram.data.moderation.REPORT_DETAILS_MAX
import com.swetdot.swetgram.data.moderation.ReportReason
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetErrorBanner
import com.swetdot.swetgram.ui.design.SwetPrimaryButton
import com.swetdot.swetgram.ui.design.SwetSecondaryButton
import com.swetdot.swetgram.ui.design.SwetTextField
import com.swetdot.swetgram.ui.profile.ProfileControlRow
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens

/**
 * SPS §16.3's report flow, drawn.
 *
 * THREE STEPS IN ONE DIALOG -- choose, confirm, receipt -- rather than three routes. A report is
 * raised from a long-press menu, a card footer or a profile button, and pushing a full screen over
 * whatever the user was looking at loses their place in a conversation or a feed for an action that
 * takes four taps.
 *
 * It is the app's dialog surface and the app's own buttons, not Material3's `AlertDialog`: SDS
 * §11.1 gives no compact button variant, so `AlertDialog`'s horizontal row of text buttons would
 * silently substitute Material's for the design system's. `ProfileDialog` and `ChatDialog` both
 * record the same finding.
 *
 * WHAT EACH STEP IS FOR
 *
 *  1. CHOOSE. Seven reasons (SPS §16.3, the contract's enum) and an optional details box capped at
 *     the contract's 1000 characters. Nothing has been sent.
 *  2. CONFIRM. Names the reason back, says a person will read it, says NOTHING IS REMOVED, and says
 *     it cannot be taken back. See `reportConfirmBody` for why each of those sentences is there.
 *  3. RECEIPT. What actually happened, plus SPS §16.2's block offered beside it with one sentence
 *     making the difference legible.
 *
 * NOT DISMISSIBLE WHILE SENDING. `Dialog`'s `onDismissRequest` is wired to a view model that
 * refuses in that state, so a back gesture mid-flight cannot leave somebody unsure whether a report
 * they can never withdraw was filed.
 */
@Composable
fun ReportSheetHost(
    viewModel: ReportViewModel,
    modifier: Modifier = Modifier,
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val request = state.request ?: return

    ReportSheet(
        request = request,
        phase = state.phase,
        onSubmit = viewModel::submit,
        onBlock = viewModel::blockAccount,
        onRetry = viewModel::retry,
        onDismiss = viewModel::dismiss,
        modifier = modifier,
    )
}

/**
 * The dialog itself, as a pure function of its inputs -- so it can be previewed and driven from a
 * test without a view model.
 *
 * [onBlock] is invoked only from the receipt and only when the request carries an account. A group
 * has no account behind it, so the offer is simply absent there rather than disabled.
 */
@Composable
fun ReportSheet(
    request: ReportRequest,
    phase: ReportPhase,
    onSubmit: (ReportReason, String?) -> Unit,
    onBlock: () -> Unit,
    onRetry: () -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val colors = SwetgramThemeTokens.colors

    // `rememberSaveable`, so turning the phone mid-report does not throw away the chosen reason,
    // the typed details or the step. An earlier pass recorded the opposite as a known cost on two
    // premises that have both expired -- see `optionalEnumSaver` in `ReportFlow.kt` for the whole
    // argument. This is the worst screen in the app to lose state on: a report cannot be taken
    // back, so somebody who has just been made to start one again may simply not.
    //
    // The String needs no saver. The enum needs one over its `name`, which is a String and so is
    // portable to every Compose Multiplatform target unchanged.
    var reason by rememberSaveable(stateSaver = optionalEnumSaver(ReportReason.entries)) {
        mutableStateOf<ReportReason?>(null)
    }
    var details by rememberSaveable { mutableStateOf("") }
    // Step 2. Local rather than in the view model, because it is a position in a form and not a
    // fact about the world -- and because the view model must not have a state that means
    // "confirmed but not sent", which is a report in limbo.
    var confirming by rememberSaveable { mutableStateOf(false) }

    val overflow = reportDetailsOverflow(details)
    val sending = phase == ReportPhase.Sending

    Dialog(
        // Refused while sending; the view model owns that rule so the host and this both obey it.
        onDismissRequest = { if (!sending) onDismiss() },
    ) {
        ReportDialogSurface(modifier) {
            when (phase) {
                is ReportPhase.Sent -> ReportReceiptStep(
                    request = request,
                    phase = phase,
                    onBlock = onBlock,
                    onDismiss = onDismiss,
                )

                is ReportPhase.Failed -> ReportFailure(
                    phase = phase,
                    onRetry = {
                        // Back to the form with the reason and the details still in it.
                        confirming = false
                        onRetry()
                    },
                    onDismiss = onDismiss,
                )

                else -> {
                    Text(
                        text = reportTitle(request),
                        style = MaterialTheme.typography.titleLarge,
                        color = colors.textPrimary,
                    )
                    Spacer(Modifier.height(Space.one))

                    val chosen = reason
                    if (confirming && chosen != null) {
                        ReportConfirmStep(
                            request = request,
                            reason = chosen,
                            details = details,
                            sending = sending,
                            onSend = { onSubmit(chosen, details) },
                            onBack = { confirming = false },
                        )
                    } else {
                        ReportChooseStep(
                            request = request,
                            reason = reason,
                            details = details,
                            overflow = overflow,
                            onReason = { reason = it },
                            onDetails = { details = it },
                            onContinue = { confirming = true },
                            onCancel = onDismiss,
                        )
                    }
                }
            }
        }
    }
}

/**
 * `ProfileDialog`'s surface, without its `Dialog`.
 *
 * The shell is split because this sheet owns its own `onDismissRequest` -- it refuses while a
 * report is in flight -- and `ProfileDialog` hard-wires dismissal to its callback. Every
 * measurement here is `ProfileDialog`'s (SDS §7.1's `radius.xl`, the main surface, the screen
 * margin), so the two are indistinguishable on screen; only the gate differs.
 *
 *
 * THE WHOLE SHEET SCROLLS, AND THE REASON LIST NO LONGER DOES.
 *
 * The reason list used to be its own scroll region under a fixed `heightIn(max = 256.dp)`, sized so
 * "seven reasons plus a details box cannot push the buttons off a small screen". Seven rows at
 * `TouchTarget.recommended` are 336dp, so that ceiling landed at 5.33 rows: the sixth row was cut
 * through the middle of its radio circle, with no scrollbar, no fade and no other hint -- and the
 * sixth row is "Illegal content", the most consequential option in the list. A bisected radio
 * button reads as a rendering fault rather than as "there is more", and somebody who does not think
 * to scroll a region INSIDE a dialog picks a worse-fitting reason from the five they can see.
 *
 * Moving the scroll out here fixes the cause rather than the symptom. A `Dialog` measures its
 * content against the window, so this Column sizes to its content and scrolls only when the content
 * genuinely does not fit -- which on any ordinary phone it does, all seven rows and the buttons. It
 * also answers the original worry properly: when it does not fit, the buttons are reachable by
 * scrolling instead of being clipped away. One scroller, not two nested in the same direction.
 *
 * WHAT THAT LEFT, AND WHAT BOUNDS IT NOW. "Sizes to its content" was the whole of the sizing rule,
 * and a `Dialog` measures against the WINDOW -- so with the keyboard up and the details field
 * focused the sheet grew past both ends of the screen: the title sat over the status bar and
 * Continue/Cancel went under the keyboard. It was recoverable, because the scroller above is real,
 * which is why this is polish rather than a blocker -- but a form whose buttons are off-screen the
 * moment somebody types in it is not finished.
 *
 * `imePadding` FIRST, so the sheet is measured against the space the keyboard leaves rather than
 * against the window it covers; then the remaining vertical safe area, so it cannot reach the
 * status bar or the gesture pill either. The second modifier sees the insets the first CONSUMED, so
 * the keyboard is not paid for twice. Horizontal is deliberately untouched: a `Dialog`'s width is
 * the platform's, and nothing here should be arguing with it.
 *
 * Both are applied OUTSIDE the scroller and outside the background, so what shrinks is the space
 * the sheet may occupy -- the single scroll region then does exactly what it was moved out here to
 * do, and the buttons stay reachable instead of being clipped away.
 *
 * The FADE is the affordance the old fixed height lacked. Painted only while there is more below,
 * and painted in `drawWithContent` so the scroll position is read in the draw phase -- reading
 * `canScrollForward` in composition would recompose the whole sheet on every scrolled pixel.
 *
 * TODO(design-system): this and `ProfileDialog` and `ChatDialog` are one component in three places.
 * It belongs in `ui/design` as a `SwetDialog` with a `dismissible` flag, which is exactly the
 * consolidation `ChatDialog`'s own note already asks for; `ui/design` was not this change's to edit.
 */
@Composable
private fun ReportDialogSurface(
    modifier: Modifier,
    content: @Composable ColumnScope.() -> Unit,
) {
    val surface = SwetgramThemeTokens.colors.surface
    val scroll = rememberScrollState()

    Column(
        modifier = modifier
            .fillMaxWidth()
            // Measured against what the keyboard leaves...
            .imePadding()
            // ...and never past the safe area at either end. See the note above for the ordering.
            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Vertical))
            .clip(RoundedCornerShape(Radius.xl))
            .background(surface)
            .drawWithContent {
                drawContent()
                if (scroll.canScrollForward) {
                    val fade = ScrollFadeHeight.toPx()
                    drawRect(
                        brush = Brush.verticalGradient(
                            colors = listOf(Color.Transparent, surface),
                            startY = size.height - fade,
                            endY = size.height,
                        ),
                        topLeft = Offset(0f, size.height - fade),
                        size = Size(size.width, fade),
                    )
                }
            }
            .verticalScroll(scroll)
            .padding(Space.three),
        verticalArrangement = Arrangement.Top,
        content = content,
    )
}

/** How much of the sheet's bottom edge the "there is more" fade covers. */
private val ScrollFadeHeight: Dp = Space.three

/** Step 1: what is wrong. */
@Composable
private fun ReportChooseStep(
    request: ReportRequest,
    reason: ReportReason?,
    details: String,
    overflow: Int,
    onReason: (ReportReason) -> Unit,
    onDetails: (String) -> Unit,
    onContinue: () -> Unit,
    onCancel: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors

    Text(
        text = reportIntro(request.target.kind),
        style = MaterialTheme.typography.bodyMedium,
        color = colors.textSecondary,
    )

    Spacer(Modifier.height(Space.two))

    Column(
        // No height ceiling and no scroller of its own: all seven rows are drawn, and the sheet
        // around them scrolls if the screen cannot take them. See [ReportDialogSurface].
        modifier = Modifier
            // One radio group, so a screen reader announces "2 of 7" rather than seven unrelated
            // switches, and so the whole ROW is the 44dp target §6.3 asks for.
            .selectableGroup(),
    ) {
        ReportReasonOrder.forEach { value ->
            val isSelected = value == reason
            ProfileControlRow(
                title = reasonLabel(value),
                modifier = Modifier.selectable(
                    selected = isSelected,
                    role = Role.RadioButton,
                    onClick = { onReason(value) },
                ),
            ) {
                RadioButton(selected = isSelected, onClick = null)
            }
        }
    }

    Spacer(Modifier.height(Space.two))

    SwetTextField(
        value = details,
        onValueChange = onDetails,
        placeholder = "Add details (optional)",
        singleLine = false,
        keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Sentences),
        // Stated rather than silently truncating a paragraph somebody took the trouble to write.
        errorText = if (overflow > 0) "At most $REPORT_DETAILS_MAX characters" else null,
    )

    Spacer(Modifier.height(Space.three))

    SwetPrimaryButton(
        text = "Continue",
        onClick = onContinue,
        enabled = canSendReport(reason, details),
    )
    Spacer(Modifier.height(Space.one))
    SwetSecondaryButton(text = "Cancel", onClick = onCancel)
}

/**
 * Step 2: the whole truth, before anything is sent.
 *
 * The confirm button is the PRIMARY treatment rather than [com.swetdot.swetgram.ui.design.SwetDestructiveButton].
 * SDS §11.1 lists "Report user" under the destructive button and the red is tempting here -- but
 * red on this button would say the REPORTER is doing something dangerous, on the one screen where
 * somebody upset is trying to ask for help. The irreversibility is carried by the sentence above
 * it, which is what §22.2 requires anyway: colour reinforces and is never the only signal.
 */
@Composable
private fun ReportConfirmStep(
    request: ReportRequest,
    reason: ReportReason,
    details: String,
    sending: Boolean,
    onSend: () -> Unit,
    onBack: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors

    Text(
        text = reportConfirmBody(
            kind = request.target.kind,
            reason = reason,
            hasDetails = details.trim().isNotEmpty(),
        ),
        style = MaterialTheme.typography.bodyMedium,
        color = colors.textSecondary,
    )

    Spacer(Modifier.height(Space.three))

    SwetPrimaryButton(
        text = "Send report",
        onClick = onSend,
        loading = sending,
    )
    Spacer(Modifier.height(Space.one))
    SwetSecondaryButton(text = "Back", onClick = onBack, enabled = !sending)
}

/**
 * Step 3: what happened, and the other half of SPS §16.
 *
 * The whole dialog becomes the receipt -- the form is gone. Leaving it up under a success message
 * invites a second identical report into a queue a person has to read, and it is the one mistake a
 * reporter cannot undo.
 */
@Composable
private fun ReportReceiptStep(
    request: ReportRequest,
    phase: ReportPhase.Sent,
    onBlock: () -> Unit,
    onDismiss: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    val account = request.account

    Text(
        // Not "Report sent", which invites "sent where, and then what". SDS §18.1: simple, direct.
        text = "Thanks for telling us",
        style = MaterialTheme.typography.titleLarge,
        color = colors.textPrimary,
    )
    Spacer(Modifier.height(Space.one))
    Text(
        text = reportReceiptBody(request.target.kind, phase.alreadyInReview),
        style = MaterialTheme.typography.bodyMedium,
        color = colors.textSecondary,
    )

    if (account != null) {
        Spacer(Modifier.height(Space.two))
        Text(
            text = blockOfferBody(account.displayName),
            style = MaterialTheme.typography.bodySmall,
            color = colors.textSecondary,
        )
        Spacer(Modifier.height(Space.two))

        when (val block = phase.block) {
            ReportBlockState.Done -> Text(
                text = "You blocked @${account.username}.",
                style = MaterialTheme.typography.bodyMedium,
                color = colors.textPrimary,
            )

            is ReportBlockState.Failed -> {
                SwetErrorBanner(message = block.message)
                Spacer(Modifier.height(Space.one))
                SwetSecondaryButton(text = "Block @${account.username}", onClick = onBlock)
            }

            else -> SwetSecondaryButton(
                text = "Block @${account.username}",
                onClick = onBlock,
                enabled = block != ReportBlockState.InFlight,
            )
        }
    }

    Spacer(Modifier.height(Space.three))
    SwetPrimaryButton(text = "Done", onClick = onDismiss)
}

/** The send failed. The form's contents are still there behind this; Try again returns to them. */
@Composable
private fun ReportFailure(
    phase: ReportPhase.Failed,
    onRetry: () -> Unit,
    onDismiss: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors

    Text(
        text = "We could not send that",
        style = MaterialTheme.typography.titleLarge,
        color = colors.textPrimary,
    )
    Spacer(Modifier.height(Space.one))
    Text(
        text = phase.message,
        style = MaterialTheme.typography.bodyMedium,
        color = colors.textSecondary,
    )
    Spacer(Modifier.height(Space.three))

    if (phase.retryable) {
        SwetPrimaryButton(text = "Try again", onClick = onRetry)
        Spacer(Modifier.height(Space.one))
        SwetSecondaryButton(text = "Close", onClick = onDismiss)
    } else {
        // No Try again over a refusal that cannot change: a missing target does not come back, and
        // a spent daily limit is not a wait anybody sits through.
        SwetPrimaryButton(text = "Close", onClick = onDismiss)
    }
}
